import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip, message } from "antd";
import { LuUnplug } from "react-icons/lu";
import { RiVolumeMuteLine, RiVolumeUpLine } from "react-icons/ri";

import Footer from "../Footer";
import SubscriptionPageContent from "../SubscriptionPageContent";
import TemplateManager from "../Templates/TemplateManager";
import { useIsMobile } from "../../hooks/useIsMobile";

import { chatHeaderTitle, formatDateDividerLabel } from "./ChatUtils";
import MessageBubble from "./MessageBubble";
import ProfilePopover from "./ProfilePopover";
import { useMessagesFromAPI } from "./useMessagesFromAPI";
import { useMessagesFromSocket } from "./useMessagesFromSocket";

export default function ChatWindow({
  currentPhone,
  isLive,
  messaging,
  contacts,
  sidebarHidden,
  onShowSidebar,
  onOpenConfigurations,
  userProfile,
  onLogout,
  mobileUnreadPulseRight = false,
  isSubscriptionRoute = false,
  onOpenSubscription,
  onOpenTemplates,
  isWabaConnected = false,
  isSubscriptionValid = true,
  lastSocketMessage,
  soundMuted = false,
  onToggleSound,
}) {
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [profilePopoverOpen, setProfilePopoverOpen] = useState(false);
  const [newMsgDividerId, setNewMsgDividerId] = useState(null);
  const [replyTo, setReplyTo] = useState(null);

  const newMsgDividerSetRef = useRef(false);
  const boxRef = useRef(null);
  const contactsRef = useRef(contacts);
  contactsRef.current = contacts;

  const isMobile = useIsMobile(700);
  const navigate = useNavigate();

  const isTemplatesRoute = window.location.pathname === "/templates";
  const activeTitle = isSubscriptionRoute
    ? "Subscription Plans"
    : isTemplatesRoute
      ? "Message Templates"
    : chatHeaderTitle(currentPhone, contacts);
  const liveColor = isLive ? "#25d366" : "#ccc";

  const scrollToBottomIfNeeded = useCallback((force) => {
    const b = boxRef.current;
    if (!b) return;
    if (force) {
      b.scrollTop = b.scrollHeight;
      return;
    }
    const nearBottom = b.scrollHeight - b.clientHeight <= b.scrollTop + 180;
    if (nearBottom) b.scrollTop = b.scrollHeight;
  }, []);

  const handleReact = (m, emoji) => {
    if (!emoji) return;
    messaging
      .sendReaction(currentPhone, m.wa_message_id, emoji, chatHeaderTitle(currentPhone, contacts))
      .catch((err) => {
        message.error(err.response?.data?.message || err.message || "Reaction failed");
      });
  };

  const {
    messageItems,
    setMessageItems,
    loading,
    showLoadMore,
    loadMoreBusy,
    topSentinelRef,
    loadMoreMessages,
  } = useMessagesFromAPI({
    boxRef,
    contactsRef,
    currentPhone,
    messaging,
    scrollToBottomIfNeeded,
  });

  useMessagesFromSocket({
    boxRef,
    currentPhone,
    lastSocketMessage,
    newMsgDividerSetRef,
    setMessageItems,
    setNewMsgDividerId,
  });

  useEffect(() => {
    if (!currentPhone) {
      newMsgDividerSetRef.current = false;
      setNewMsgDividerId(null);
      return;
    }

    newMsgDividerSetRef.current = false;
    setNewMsgDividerId(null);
  }, [currentPhone]);

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;

    const handleClick = () => {
      if (newMsgDividerSetRef.current) {
        newMsgDividerSetRef.current = false;
        setNewMsgDividerId(null);
      }
    };

    box.addEventListener("click", handleClick);
    return () => box.removeEventListener("click", handleClick);
  }, []);

  const clearNewMsgDivider = useCallback(() => {
    newMsgDividerSetRef.current = false;
    setNewMsgDividerId(null);
  }, []);

  let lastDayKey = "";
  const timeline = [];

  messageItems.forEach((m, idx) => {
    const d = new Date(m.timestamp);
    const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

    if (dayKey !== lastDayKey) {
      timeline.push(
        <div key={`div-${dayKey}`} className="date-divider">
          {formatDateDividerLabel(m.timestamp)}
        </div>
      );
      lastDayKey = dayKey;
    }

    if (newMsgDividerId === m.sk) {
      timeline.push(
        <div key={`new-msg-divider-${m.sk}`} className="new-msg-divider">
          New Messages
        </div>
      );
    }

    timeline.push(
      <MessageBubble
        key={m.sk || `${m.wa_message_id}-${idx}`}
        m={m}
        onReply={(msg) => setReplyTo(msg)}
        onReact={handleReact}
      />
    );
  });

  return (
    <div id="main-chat" data-testid="chat-main-container">
      {mobileUnreadPulseRight ? (
        <span
          className="mobile-screen-unread mobile-screen-unread--right blinking-unread"
          title="Unread messages"
          aria-hidden
        >
          ✉
        </span>
      ) : null}

      <div className="header chat-main-header">
        <div className="chat-header-left">
          <button
            type="button"
            id="mobileBackBtn"
            className="chat-back-btn"
            onClick={onShowSidebar}
            style={{
              display: isMobile && sidebarHidden ? "flex" : "none",
            }}
            aria-label="Back to chats"
          >
            ←
          </button>
          <span
            id="activeContactName"
            data-testid="active-contact-name"
            title={activeTitle}
          >
            {activeTitle}
          </span>
        </div>

        <div className="chat-header-actions">
          <Tooltip title={
            !isWabaConnected 
              ? "Connect Whatsapp Business account to get started" 
              : !isSubscriptionValid 
                ? "Subscription required to send messages" 
                : ""
          }>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: (!isWabaConnected || !isSubscriptionValid) ? "help" : "default",
              }}
            >
              <div
                id="pulse"
                style={{
                  width: 8,
                  height: 8,
                  background: liveColor,
                  borderRadius: "50%",
                }}
                className={isLive ? "blinking" : ""}
              />
              <span
                id="liveStatusText"
                style={{ color: isLive ? liveColor : "#667781" }}
              >
                {isLive ? "LIVE" : "OFFLINE"}
              </span>
            </div>
          </Tooltip>

          {onOpenConfigurations ? (
            <button
              data-testid="configurations-button"
              type="button"
              className="chat-header-icon-btn chat-configurations-btn"
              onClick={() => onOpenConfigurations()}
              title="Configurations"
              aria-label="Open Configurations"
            >
              <LuUnplug size={24} />
            </button>
          ) : null}

          {onToggleSound ? (
            <Tooltip title={soundMuted ? "Unmute notification sounds" : "Mute notification sounds"}>
              <button
                type="button"
                className="chat-header-icon-btn"
                onClick={onToggleSound}
                title={soundMuted ? "Unmute notification sounds" : "Mute notification sounds"}
                aria-label={soundMuted ? "Unmute notification sounds" : "Mute notification sounds"}
              >
                {soundMuted ? (
                  <RiVolumeMuteLine size={22} />
                ) : (
                  <RiVolumeUpLine size={22} />
                )}
              </button>
            </Tooltip>
          ) : null}

          <ProfilePopover
            open={profilePopoverOpen}
            onOpenChange={setProfilePopoverOpen}
            userProfile={userProfile}
            onLogout={onLogout}
            onOpenTemplates={onOpenTemplates}
            isWabaConnected={isWabaConnected}
            isSubscriptionValid={isSubscriptionValid}
          />

          {(isSubscriptionRoute || isTemplatesRoute) && (
            <button
              type="button"
              className="chat-header-icon-btn"
              onClick={() => navigate("/")}
              title="Back to Chats"
              aria-label="Back to Chats"
              style={{ fontSize: "20px", fontWeight: "bold" }}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {isSubscriptionRoute ? (
        <SubscriptionPageContent
          onOpenSubscriptionModal={onOpenSubscription}
          userProfile={userProfile}
        />
      ) : isTemplatesRoute ? (
        <TemplateManager messaging={messaging} userProfile={userProfile} />
      ) : (
        <>
          <div id="chat-box" data-testid="chat-box" ref={boxRef}>
            <div
              ref={topSentinelRef}
              className="chat-load-sentinel"
              aria-hidden
            />

            <button
              data-testid="load-more-button"
              id="loadMoreBtn"
              className="load-more-btn"
              style={{
                display: showLoadMore && currentPhone ? "block" : "none",
              }}
              onClick={loadMoreMessages}
              disabled={loadMoreBusy}
            >
              {loadMoreBusy ? "Loading earlier…" : "Load earlier messages"}
            </button>

            <div
              id="loadingIndicator"
              style={{ display: loading ? "block" : "none" }}
            >
              Loading messages…
            </div>

            <div id="messages-container" data-testid="messages-container" className="messages-stack">
              {timeline}
            </div>
          </div>

          <Footer
            currentPhone={currentPhone}
            messaging={messaging}
            contacts={contacts}
            emojiOpen={emojiOpen}
            setEmojiOpen={setEmojiOpen}
            onUserInteraction={clearNewMsgDivider}
            replyTo={replyTo}
            onCancelReply={() => setReplyTo(null)}
            isLive={isLive}
          />
        </>
      )}
    </div>
  );
}
